import { motion } from 'framer-motion';
import React from 'react';

import { COLORS } from '@constants/colors';

import { generateAnchor } from '@utils/generateAnchor';

interface HeroProps {
  title: React.ReactNode;
  description: string;
  icon?: React.ReactNode;
  showBreadcrumb?: boolean;
  breadcrumbItems?: Array<{ label: string; href?: string }>;
  variant?: 'simple' | 'gradient';
  children?: React.ReactNode;
}

export const Hero: React.FC<HeroProps> = ({
  title,
  description,
  icon,
  showBreadcrumb = false,
  breadcrumbItems = [],
  variant = 'gradient',
  children,
}) => {
  const titleAnchor = title ? generateAnchor(title.toString()) : '';

  if (variant === 'simple') {
    return (
      <motion.section
        className="py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              id={titleAnchor}
            >
              {title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {description}
            </p>
          </motion.div>
          {children}
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      className="relative pt-20 sm:pt-32 pb-8 sm:pb-12 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full border border-white/20"></div>
        <div className="absolute top-40 right-20 w-24 h-24 rounded-full border border-white/20"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full border border-white/20"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          className="text-center max-w-4xl mx-auto"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {icon && (
            <div className="flex items-center justify-center mb-6">
              {icon}
              <h1
                className="text-4xl md:text-5xl font-bold text-white ml-4"
                id={titleAnchor}
              >
                {title}
              </h1>
            </div>
          )}
          {!icon && (
            <h1
              className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
              id={titleAnchor}
            >
              {title}
            </h1>
          )}
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            {description}
          </p>

          {showBreadcrumb && breadcrumbItems.length > 0 && (
            <div className="flex items-center justify-center text-white/60 text-sm">
              {breadcrumbItems.map((item, index) => (
                <React.Fragment key={index}>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="hover:text-white/80 transition-colors"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <span className="text-white/80">{item.label}</span>
                  )}
                  {index < breadcrumbItems.length - 1 && (
                    <span className="mx-2">›</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {children}
        </motion.div>
      </div>
    </motion.section>
  );
};
